import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Store,
  TrendingUp,
  Wallet,
  MapPin,
  PackagePlus,
  UserPlus,
  ClipboardList,
  ShieldCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const benefits = [
  {
    icon: TrendingUp,
    title: "Reach More Customers",
    desc: "List your organic products in front of thousands of buyers across Tamil Nadu.",
  },
  {
    icon: PackagePlus,
    title: "Easy Product Listing",
    desc: "Add products with multiple images, pricing, stock and SEO details in minutes.",
  },
  {
    icon: Wallet,
    title: "Secure Payments",
    desc: "Get paid safely through Razorpay with transparent order tracking.",
  },
  {
    icon: MapPin,
    title: "Location-Based Selling",
    desc: "Sell to nearby districts with our built-in Tamil Nadu location filtering.",
  },
];

const steps = [
  {
    icon: UserPlus,
    title: "1. Register as a Vendor",
    desc: "Sign up with your phone number — your account is created as a vendor instantly.",
  },
  {
    icon: PackagePlus,
    title: "2. Add Your Products",
    desc: "Upload product photos, set actual & offer prices, stock and category.",
  },
  {
    icon: ClipboardList,
    title: "3. Manage Orders & Get Paid",
    desc: "Track incoming orders, fulfil them, and receive secure payouts.",
  },
];

export default function BecomeVendorPage() {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    document.title = "Sell on OrgShop - Become a Vendor | Tamil Nadu Organic Marketplace";
  }, []);

  const isVendor = user && (user.role === "vendor" || user.role === "admin");

  return (
    <div data-testid="become-vendor-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-primary text-primary-foreground" data-testid="vendor-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-accent/20 text-accent rounded-full px-4 py-1.5 text-sm font-medium mb-5">
              <Store className="w-4 h-4" /> Sell on OrgShop
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-5 leading-tight" data-testid="vendor-hero-title">
              Grow your organic business with OrgShop
            </h1>
            <p className="text-base md:text-lg text-primary-foreground/85 mb-8 leading-relaxed">
              Join Tamil Nadu's trusted marketplace for wood-pressed oils and organic products.
              Reach more customers, list products easily, and get paid securely.
            </p>
            {isVendor ? (
              <Link to="/vendor">
                <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium" data-testid="vendor-go-dashboard-btn">
                  Go to Vendor Dashboard
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            ) : (
              <Button
                onClick={() => setShowAuth(true)}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium"
                data-testid="vendor-register-hero-btn"
              >
                Register as Vendor
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            )}
          </div>
          <div className="rounded-2xl overflow-hidden shadow-hover hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=900&q=80"
              alt="Organic cooking oil"
              className="w-full h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-background" data-testid="vendor-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">Why sell with us?</h2>
            <p className="text-base text-muted-foreground">Everything you need to start and grow online</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-card rounded-2xl p-7 shadow-soft hover:shadow-hover transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <b.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-background-alt" data-testid="vendor-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">How to get started</h2>
            <p className="text-base text-muted-foreground">Start selling in three simple steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.title} className="bg-card rounded-2xl p-8 shadow-soft text-center">
                <div className="w-16 h-16 bg-accent/15 rounded-full flex items-center justify-center mx-auto mb-5">
                  <s.icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{s.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground" data-testid="vendor-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto mb-5 text-accent" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-5">Ready to start selling?</h2>
          <p className="text-base md:text-lg text-primary-foreground/85 mb-8">
            Create your vendor account today — it only takes a minute.
          </p>
          {isVendor ? (
            <Link to="/vendor">
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium" data-testid="vendor-go-dashboard-cta-btn">
                Go to Vendor Dashboard
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          ) : (
            <Button
              onClick={() => setShowAuth(true)}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="vendor-register-cta-btn"
            >
              Register as Vendor
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          )}
        </div>
      </section>

      <Footer />

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} asVendor />
    </div>
  );
}
